/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import html2canvas from "html2canvas-pro";
import { jsPDF } from "jspdf";

const MARGIN_MM = 10;
const CANVAS_SCALE = 2;
const JPEG_QUALITY = 0.92;

function captureViewport(element: HTMLElement): { windowWidth: number; windowHeight: number } {
  const rect = element.getBoundingClientRect();
  const windowWidth = Math.max(
    1,
    Math.ceil(element.scrollWidth),
    Math.ceil(element.clientWidth),
    Math.ceil(rect.width)
  );
  const windowHeight = Math.max(
    1,
    Math.ceil(element.scrollHeight),
    Math.ceil(element.clientHeight),
    Math.ceil(rect.height)
  );
  return { windowWidth, windowHeight };
}

/** Rasterise DOM to A4 PDF; width is fit to printable area using canvas pixel aspect ratio. */
export async function htmlElementToPdf(element: HTMLElement): Promise<jsPDF> {
  const { windowWidth, windowHeight } = captureViewport(element);

  const canvas = await html2canvas(element, {
    scale: CANVAS_SCALE,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
    windowWidth,
    windowHeight,
  });

  const imgData = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - 2 * MARGIN_MM;
  const usableH = pageH - 2 * MARGIN_MM;

  const imgWMm = usableW;
  const imgHMm = (canvas.height * imgWMm) / canvas.width;

  let heightLeft = imgHMm;
  let y = MARGIN_MM;

  pdf.addImage(imgData, "JPEG", MARGIN_MM, y, imgWMm, imgHMm);
  heightLeft -= usableH;

  while (heightLeft > 0) {
    y = MARGIN_MM + (heightLeft - imgHMm);
    pdf.addPage();
    pdf.addImage(imgData, "JPEG", MARGIN_MM, y, imgWMm, imgHMm);
    heightLeft -= usableH;
  }

  return pdf;
}
